import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Download, FileText, Languages, ListChecks, PencilLine, Rows3, Save, Sparkles } from "lucide-react";
import { useApi, type AiProposal, type EcInvestigationReport, type EcReportVariant, type ReportText } from "@ncp/api-client";
import { useLocale, useTranslation } from "@ncp/i18n";
import { Badge, Button, Card, CardContent, CardDescription, CardHeader, CardTitle, EmptyState, InlineAlert, Skeleton, Textarea, cn, toast, InfoHint, notifySaveFailed, useSave } from "@ncp/ui";
import { DraftSources, DraftTextEnd, RedraftAction } from "@ncp/assistant-sdk";
import type { EcFormView } from "../api";
import { GuidedReview } from "../report/GuidedReview";
import { useIsNextStep } from "../stage/nextStep";
import { buildSections, isEmptyText, writeText, type ReportSection, type SectionDecision } from "../report/reportSections";
import { markSets, useCalculationMethod } from "../figures";
import { useConfirmAction } from "../../../components/confirmAction";

/**
 * The Ministry's investigation report on an economic concentration. The agent drafts; the economic expert reviews it passage by
 * passage in a guided pass — accept, or override in Arabic and English — and only then finalises, which attaches the Arabic PDF
 * and the editable Word version to the case file. Nothing the agent wrote is applied until an officer has been through it.
 */
export function InvestigationReportTab({ caseId, caseState, caseStateLabel, proposals }: { caseId: string; caseState: string; caseStateLabel: { en: string; ar: string }; proposals: AiProposal[] }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const api = useApi();
  const qc = useQueryClient();

  const reports = useMemo(
    () => proposals.filter((p) => p.kind === "EcInvestigationReport").sort((a, b) => b.createdAt.localeCompare(a.createdAt)),
    [proposals],
  );
  const latest = reports[0];
  const pending = latest?.status === "Proposed" ? latest : undefined;
  const readOnly = !pending;

  // Condition 3.10: the report counts as read once the end of its text has been on screen – the last passage of the guided pass, or the end of
  // the whole report – and its sources as opened once one of them is opened below; never on loading the tab.

  const isNext = useIsNextStep("analysis:report");
  const [mode, setMode] = useState<"guided" | "list">("guided");
  const [view, setView] = useState<"ar" | "en">(locale === "en" ? "en" : "ar");
  const [draft, setDraft] = useState<EcInvestigationReport | null>(null);
  const [decisions, setDecisions] = useState<Record<string, SectionDecision>>({});
  const [dirty, setDirty] = useState(false);
  const [reasons, setReasons] = useState("");

  // The figures behind each passage: the control test, the stake and the consideration live on the notification form.
  const form = useQuery({ queryKey: ["ec-form", caseId], queryFn: () => api.get<EcFormView>(`/api/ec/${caseId}`) });

  // A newly arrived draft replaces whatever was on screen; a finalised report is shown read-only. The reviewer's decisions and edits on
  // a pending draft are kept on the device until it is finalised, so a reload or an expired session does not lose the pass.
  const passKey = pending ? `ncp.reportPass.${pending.id}` : null;
  useEffect(() => {
    const source = (pending ?? latest)?.structured as EcInvestigationReport | undefined;
    const kept = passKey ? readPass(passKey) : null;
    setDraft(kept?.draft ?? (source ? structuredClone(source) : null));
    setDecisions(kept?.decisions ?? {});
    setDirty(kept?.dirty ?? false);
    setReasons(kept?.reasons ?? "");
  }, [pending?.id, latest?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!passKey || !draft) return;
    const empty = Object.keys(decisions).length === 0 && !dirty && !reasons.trim();
    try {
      if (empty) window.localStorage.removeItem(passKey);
      else window.localStorage.setItem(passKey, JSON.stringify({ draft, decisions, dirty, reasons } satisfies KeptPass));
    } catch { /* storage unavailable or full */ }
  }, [passKey, draft, decisions, dirty, reasons]);

  const run = useSave({
    mutationFn: (variant: EcReportVariant) => api.post(`/api/ai/cases/${caseId}/run`, { agentKey: "ec-investigation-report", input: { variant } }),
    onSuccess: () => toast.info(t("backoffice:investigationReport.drafting"), { description: t("backoffice:investigationReport.draftingHint") }),
  });

  const save = useSave({
    mutationFn: () => api.put(`/api/ai/proposals/${pending!.id}/structured`, { structuredJson: JSON.stringify(draft) }),
    saved: t("actions.saved"),
    onSuccess: () => { setDirty(false); void qc.invalidateQueries({ queryKey: ["case-proposals", caseId] }); },
  });

  const confirmAction = useConfirmAction();

  const finalise = useSave({
    // The passage-by-passage pass goes with the approval: the server refuses a report with a drafted passage neither accepted nor overridden.
    mutationFn: () => api.post<{ succeeded: boolean; error?: string; actionResult?: string }>(`/api/ai/proposals/${pending!.id}/approve`, {
      editedStructuredJson: JSON.stringify({ ...draft, passageReview: Object.fromEntries(Object.entries(decisions).filter((e): e is [string, "accepted" | "overridden"] => e[1] !== "pending")) }),
      note: reasons.trim(),
    }),
    saved: (r) => ({ title: t("backoffice:investigationReport.finalised"), description: r.actionResult ?? undefined }),
    onSuccess: () => {
      if (passKey) try { window.localStorage.removeItem(passKey); } catch { /* storage unavailable */ }
      for (const k of [["case-proposals", caseId], ["case", caseId], ["case-documents", caseId], ["case-workflow", caseId]]) void qc.invalidateQueries({ queryKey: k });
    },
  });

  // The approved version goes on the file in Arabic and in its English mirror (EC-17.02); a rendering that failed at approval is attached again here.
  const attachRenderings = useSave({
    mutationFn: () => api.post<{ stored: string[] }>(`/api/cases/${caseId}/reports/${latest!.id}/renderings`, {}),
    saved: (r) => (r.stored.length > 0 ? t("backoffice:investigationReport.renderingsAttached", { files: r.stored.join(", ") }) : t("backoffice:investigationReport.renderingsUpToDate")),
    onSuccess: () => {
      for (const k of [["case-documents", caseId], ["case", caseId], ["case-review", caseId]]) void qc.invalidateQueries({ queryKey: k });
    },
  });

  const exportDoc = (format: "pdf" | "docx") => {
    const proposal = pending ?? latest;
    if (!proposal) return;
    void api.download(`/api/cases/${caseId}/reports/${proposal.id}/export?format=${format}&locale=${view}`)
      .catch((e: unknown) => notifySaveFailed(e, t("misc.error")));
  };

  // The evidence beside each passage is read against the published method's marks (condition 2.9): the ΔHHI mark, the HHI bands, the stakes.
  const bands = useCalculationMethod().data?.bands;
  const sections = useMemo(() => {
    if (!draft) return [];
    const m = markSets(bands);
    return buildSections(draft, form.data, t, locale, bands ? { deltaHhiSignificant: bands.deltaHhiSignificant, hhiMarks: m.hhi, deltaHhiMarks: m.delta, controlMarks: m.control } : {});
  }, [draft, form.data, t, locale, bands]);

  const decide = (path: string, decision: SectionDecision, value?: ReportText) => {
    if (value) { setDraft((d) => (d ? writeText(d, path, value) : d)); setDirty(true); }
    setDecisions((d) => ({ ...d, [path]: decision }));
  };

  if (reports.length === 0) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base"><FileText className="size-4 text-aegold-700" /> {t("backoffice:investigationReport.title")}<InfoHint>{t("backoffice:investigationReport.intro")}</InfoHint></CardTitle>
        </CardHeader>
        <CardContent>
          <EmptyState icon={FileText} title={t("backoffice:investigationReport.none")} description={t("backoffice:investigationReport.noneHint")} className="py-10" action={
            <div className="flex flex-wrap justify-center gap-2">
              <Button variant="gold" primary={isNext} loading={run.isPending} onClick={() => run.mutate("SubstantiveReview")}><Sparkles /> {t("backoffice:investigationReport.draftSubstantive")}</Button>
              <Button variant="outline" loading={run.isPending} onClick={() => run.mutate("ThresholdsUnmet")}><Sparkles /> {t("backoffice:investigationReport.draftThresholds")}</Button>
            </div>
          } />
        </CardContent>
      </Card>
    );
  }

  if (!draft) return <Skeleton className="h-96" />;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-2xl border border-sand-200 bg-white px-4 py-2.5">
        <FileText className="size-4 shrink-0 text-aegold-700" />
        <p className="flex min-w-0 flex-wrap items-center gap-2 text-sm font-semibold">
          {t("backoffice:investigationReport.title")}
          <Badge size="sm" variant={draft.variant === "ThresholdsUnmet" ? "warning" : "gold"}>{t(`backoffice:investigationReport.variants.${draft.variant}`)}</Badge>
          {readOnly ? <Badge size="sm" variant="success"><Check /> {t("backoffice:investigationReport.finalisedBadge")}</Badge> : <Badge size="sm" variant="ai" dot pulse>{t("ai.pendingReview")}</Badge>}
        </p>
        <div className="ms-auto flex flex-wrap items-center gap-2">
          <div className="flex rounded-lg border border-sand-200 p-0.5">
            <ModeButton active={mode === "guided"} onClick={() => setMode("guided")} icon={ListChecks} label={t("backoffice:investigationReport.modeGuided")} />
            <ModeButton active={mode === "list"} onClick={() => setMode("list")} icon={Rows3} label={t("backoffice:investigationReport.modeList")} />
          </div>
          <Button size="sm" variant="ghost" onClick={() => setView(view === "ar" ? "en" : "ar")}><Languages /> {view === "ar" ? t("backoffice:investigationReport.versionAr") : t("backoffice:investigationReport.versionEn")}</Button>
          <Button size="sm" variant="outline" onClick={() => exportDoc("pdf")}><Download /> PDF</Button>
          <Button size="sm" variant="outline" onClick={() => exportDoc("docx")}><Download /> {t("backoffice:investigationReport.word")}</Button>
          {readOnly ? <Button size="sm" variant="outline" loading={attachRenderings.isPending} onClick={() => attachRenderings.mutate()}><FileText /> {t("backoffice:investigationReport.attachRenderings")}</Button> : <Button size="sm" variant="secondary" disabled={!dirty} loading={save.isPending} onClick={() => save.mutate()}><Save /> {t("actions.save")}</Button>}
        </div>
      </div>

      {readOnly ? <InlineAlert tone="success">{t("backoffice:investigationReport.finalisedHint")}</InlineAlert> : null}
      {caseState === "Assessment" || caseState === "UndersecretaryDecision" ? null : (
        <InlineAlert tone="info">{t("backoffice:investigationReport.stateHint", { state: locale === "ar" ? caseStateLabel.ar : caseStateLabel.en })}</InlineAlert>
      )}

      {mode === "guided" ? (
        <GuidedReview
          report={draft}
          sections={sections}
          decisions={decisions}
          view={view}
          readOnly={readOnly}
          busy={finalise.isPending}
          onDecide={decide}
          onRecommendation={(code) => { setDraft((d) => (d ? { ...d, recommendationCode: code } : d)); setDirty(true); }}
          reasons={readOnly ? latest?.reviewNote ?? "" : reasons}
          onReasons={setReasons}
          onFinalise={() => void confirmAction("finaliseReport", () => finalise.mutate())}
          onRedraft={() => run.mutate(draft.variant)}
          textEnd={<DraftTextEnd proposal={pending} />}
        />
      ) : (
        <div className="space-y-3">
          {sections.map((section) => (
            <ListSectionCard
              key={section.path}
              section={section}
              draftName={pending ? `proposal:${pending.id}:passage:${section.path}` : null}
              view={view}
              readOnly={readOnly}
              decision={decisions[section.path] ?? "pending"}
              onDecide={decide}
            />
          ))}
          <DraftTextEnd proposal={pending} />
        </div>
      )}
      <DraftSources proposal={pending} />
    </div>
  );
}

interface KeptPass { draft: EcInvestigationReport; decisions: Record<string, SectionDecision>; dirty: boolean; reasons?: string }

function readPass(key: string): KeptPass | null {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as KeptPass) : null;
  } catch {
    return null;
  }
}

function ModeButton({ active, onClick, icon: Icon, label }: { active: boolean; onClick: () => void; icon: typeof ListChecks; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn("flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium transition-colors", active ? "bg-aeblack-950 text-white" : "text-sand-700 hover:bg-sand-100")}
    >
      <Icon className="size-3.5" /> {label}
    </button>
  );
}

/** The whole report on one page, for reviewers who prefer to scan before deciding. */
function ListSectionCard({ section, draftName, view, readOnly, decision, onDecide }: {
  section: ReportSection;
  draftName: string | null;
  view: "ar" | "en";
  readOnly: boolean;
  decision: SectionDecision;
  onDecide: (path: string, decision: SectionDecision, value?: ReportText) => void;
}) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(section.value);
  // The passage as the draft holds it, except while the officer is rewriting it: a rebuilt or refetched draft never replaces their text (S 2.7).
  useEffect(() => { if (!editing) setText(section.value); }, [section.value.en, section.value.ar, editing]); // eslint-disable-line react-hooks/exhaustive-deps
  const empty = isEmptyText(section.value);
  const body = view === "ar" ? section.value.ar : section.value.en || section.value.ar;

  return (
    <Card className={cn(section.conclusion && "border-aegold-300", decision === "accepted" && "border-success-300 bg-success-50/30", decision === "overridden" && "border-aegold-400 bg-aegold-50/30")}>
      <CardHeader className="flex-row items-start justify-between gap-3 pb-2">
        <div className="min-w-0">
          <CardTitle className="text-sm font-semibold text-aeblack-900">{section.label}</CardTitle>
          <CardDescription className={cn(empty && "text-warning-700")}>{empty ? t("backoffice:investigationReport.emptySection") : section.hint}</CardDescription>
        </div>
        {readOnly ? null : (
          <div className="flex shrink-0 gap-1.5">
            {decision === "pending" ? (
              <Button size="sm" variant="ghost" disabled={empty} onClick={() => onDecide(section.path, "accepted")}><Check /> {t("actions.accept")}</Button>
            ) : (
              <Badge size="sm" variant={decision === "accepted" ? "success" : "gold"}>{t(`backoffice:investigationReport.${decision}`)}</Badge>
            )}
            <Button size="sm" variant="ghost" onClick={() => setEditing((e) => !e)}><PencilLine /> {editing ? t("actions.cancel") : t("backoffice:investigationReport.override")}</Button>
          </div>
        )}
      </CardHeader>
      <CardContent>
        {editing ? (
          <div className="space-y-2">
            <div className="flex items-center justify-between gap-2">
              <label htmlFor={`section-ar-${section.path}`} className="text-[11px] font-semibold uppercase tracking-wider text-sand-600">العربية</label>
              <RedraftAction format="plain" getText={() => text.ar} onApply={(v) => setText((x) => ({ ...x, ar: v }))} context={{ kind: `A passage of the investigation report, in Arabic (${section.chapter})`, title: section.label, guidance: section.hint, reference: text.en || undefined }} />
            </div>
            <Textarea id={`section-ar-${section.path}`} dir="rtl" rows={5} value={text.ar} onChange={(e) => setText({ ...text, ar: e.target.value })} draft={draftName && `${draftName}:ar`} />
            <div className="flex items-center justify-between gap-2">
              <label htmlFor={`section-en-${section.path}`} className="text-[11px] font-semibold uppercase tracking-wider text-sand-600">English</label>
              <RedraftAction format="plain" getText={() => text.en} onApply={(v) => setText((x) => ({ ...x, en: v }))} context={{ kind: `A passage of the investigation report, in English (${section.chapter})`, title: section.label, guidance: section.hint, reference: text.ar || undefined }} />
            </div>
            <Textarea id={`section-en-${section.path}`} dir="ltr" rows={4} value={text.en} onChange={(e) => setText({ ...text, en: e.target.value })} draft={draftName && `${draftName}:en`} />
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>{t("actions.cancel")}</Button>
              <Button size="sm" variant="gold" onClick={() => { onDecide(section.path, "overridden", text); setEditing(false); }}><Check /> {t("backoffice:investigationReport.applyOverride")}</Button>
            </div>
          </div>
        ) : (
          <p className="whitespace-pre-wrap text-sm leading-relaxed" dir={view === "ar" ? "rtl" : "ltr"}>{body || "—"}</p>
        )}
      </CardContent>
    </Card>
  );
}
